"use client";

import { Heart, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function StayUpdatedForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="mx-auto max-w-md rounded-lg border border-border bg-background/60 p-6 text-center">
        <p className="font-default text-xl text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Sign up another email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-3">
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem className="flex-1">
              <FormLabel className="sr-only">First Name</FormLabel>
              <FormControl>
                <Input type="text" placeholder="Your name" className="h-12 rounded-md border-0 border-b border-border bg-transparent px-0 text-lg focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/70" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem className="flex-[2]">
              <FormLabel className="sr-only">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="your@email.com" className="h-12 rounded-md border-0 border-b border-border bg-transparent px-0 text-lg focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/70" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <Button type="submit" disabled={isSubmitting} className="h-12 rounded-lg bg-primary px-8 text-primary-foreground hover:bg-primary/90">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Sending
            </> : "Get Tracker"}
        </Button>
      </div>

      <FormField control={control} name="consent_updates" rules={{
      required: "Your consent is required to subscribe"
    }} render={({
      field
    }) => <FormItem className="flex items-start gap-3">
            <FormControl>
              <input type="checkbox" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} className="mt-1 size-4 rounded border-border accent-primary" />
            </FormControl>
            <div className="flex-1">
              <FormLabel className="text-sm font-normal text-muted-foreground leading-relaxed cursor-pointer">
                I would like to receive pet health tips, weight management guides, and exclusive offers from Fit Pet Guide.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <p className="text-center text-xs italic text-muted-foreground">
        Join 5,000+ pet parents. Unsubscribe anytime.
      </p>
    </Form>;
}
export default function FreePetWeightTrackerSignup() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const trustBadges = [{
    id: 1,
    name: "Vet Approved"
  }, {
    id: 2,
    name: "Nutritionist Verified"
  }, {
    id: 3,
    name: "Pet Health Certified"
  }];
  return <section data-section-id="5103" id="free-pet-weight-tracker-signup" className="dark relative overflow-hidden bg-background py-32 md:py-40 lg:py-[10rem]">
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.05]">
        <div className="size-[600px] md:size-[800px] rounded-lg border border-foreground" style={{
        borderRadius: "9999px"
      }} />
        <div className="absolute left-1/2 top-1/2 size-[400px] md:size-[560px] -translate-x-1/2 -translate-y-1/2 border border-foreground" style={{
        borderRadius: "9999px"
      }} />
      </div>

      <div ref={ref} className={`relative z-10 mx-auto max-w-[640px] px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="mb-10 flex justify-center">
          <div className="relative flex size-16 items-center justify-center">
            <div className="absolute inset-0 border border-primary/40" style={{
            borderRadius: "9999px"
          }} />
            <div className="absolute inset-2 border border-primary/20 animate-pulse" style={{
            borderRadius: "9999px"
          }} />
            <Heart className="relative size-7 text-primary" strokeWidth={1.25} />
          </div>
        </div>

        <h2 className="text-center font-default text-foreground font-normal tracking-tight">
          <span className="block text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05]">
            Download your free
          </span>
          <span className="block text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] italic">
            Pet Weight Tracker.
          </span>
        </h2>

        <p className="mx-auto mt-8 max-w-lg text-center text-base text-muted-foreground leading-relaxed">
          Start your pet's journey to a healthier weight today. Get our simple, printable tracker and expert tips delivered straight to your inbox.
        </p>

        <div className="mt-12">
          <FormProvider formKey="pet_weight_tracker_signup_home" formId="pet_weight_tracker_signup_home" sectionName="free-pet-weight-tracker-signup" defaultValues={{
          first_name: "",
          email: "",
          consent_updates: false
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          },
          consent_updates: {
            required: true
          }
        }} submitText="Get Tracker" successMessage="Success! Check your inbox for your free tracker." errorMessage="Subscription failed. Please try again.">
            <StayUpdatedForm />
          </FormProvider>
        </div>

        <div className="mt-16 flex flex-col items-center gap-4 border-t border-border pt-10">
          <p className="text-xs uppercase tracking-widest text-muted-foreground/70">
            Trusted by experts
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 opacity-60">
            {trustBadges.map((badge, i) => <div key={badge.id} data-index={i} className="flex items-center gap-6">
                <span className="font-default text-sm md:text-base italic text-foreground">
                  {badge.name}
                </span>
                {i < trustBadges.length - 1 && <span className="text-muted-foreground" aria-hidden="true">·</span>}
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}